import type { RenderSegment, ReviewSample } from '../../api/rendersApi';

export const REQUIRED_LISTEN_RATIO = 0.8;

export interface ApprovalListenState {
  listenedSeconds: number;
  introListened: boolean;
  goodnightListened: boolean;
}

export type ReviewSampleProgress = Record<string, number>;

const REQUIRED_STORY_SAMPLE_IDS = ['opening', 'middle', 'closing'] as const;

export function addReviewSampleProgress(
  samples: ReviewSample[],
  progress: ReviewSampleProgress,
  previousSeconds: number,
  currentSeconds: number
): ReviewSampleProgress {
  const next = { ...progress };
  samples.forEach((sample) => {
    const overlap = Math.max(
      0,
      Math.min(currentSeconds, sample.end_seconds)
        - Math.max(previousSeconds, sample.start_seconds)
    );
    if (overlap > 0) {
      next[sample.sample_id] = Math.min(
        sample.required_seconds,
        (next[sample.sample_id] || 0) + overlap
      );
    }
  });
  return next;
}

export function completedReviewSampleIds(
  samples: ReviewSample[],
  progress: ReviewSampleProgress
): string[] {
  return samples
    .filter((sample) => (progress[sample.sample_id] || 0) >= sample.required_seconds)
    .map((sample) => sample.sample_id);
}

export function canApproveAfterReviewSamples(
  samples: ReviewSample[],
  completedIds: string[]
): boolean {
  const availableIds = new Set(samples.map((sample) => sample.sample_id));
  const completed = new Set(completedIds);
  return REQUIRED_STORY_SAMPLE_IDS.every((sampleId) => availableIds.has(sampleId))
    && samples.every((sample) => completed.has(sample.sample_id));
}

export function approvalRuntimeSeconds(
  outputSeconds: number | null | undefined,
  segments: RenderSegment[]
): number {
  if (outputSeconds && outputSeconds > 0) return outputSeconds;
  return Math.max(0, ...segments.map((segment) => segment.end_ms / 1000));
}

export function introCheckpointSeconds(
  runtimeSeconds: number,
  segments: RenderSegment[]
): number {
  const firstStoryStart = Math.min(
    ...segments.map((segment) => segment.start_ms / 1000)
  );
  if (Number.isFinite(firstStoryStart) && firstStoryStart >= 2) {
    return firstStoryStart;
  }
  return Math.min(8, Math.max(2, runtimeSeconds * 0.03));
}

export function listenedRatio(
  listenedSeconds: number,
  runtimeSeconds: number
): number {
  if (runtimeSeconds <= 0) return 0;
  return Math.min(1, Math.max(0, listenedSeconds / runtimeSeconds));
}

export function canApproveAfterListen(
  state: ApprovalListenState,
  runtimeSeconds: number
): boolean {
  return (
    state.introListened &&
    state.goodnightListened &&
    listenedRatio(state.listenedSeconds, runtimeSeconds) >= REQUIRED_LISTEN_RATIO
  );
}
